import { RefObject, useCallback, useEffect, useRef } from "react";

import { createRafThrottle } from "./rafThrottle";

// For a page/section whose pagination follows the PAGE's own scroll instead of a table's inner scroll
// (originally built for Procedures, now shared by any list screen wired the same way): this is the same
// "near the bottom" threshold ChatList.tsx already uses for its own section-scroll pagination
// (ChatList.tsx:30,56-61), not the Table component's own (unrelated) debounced onFinishScroll.
const SECTION_SCROLL_BOTTOM_THRESHOLD_PX = 50;

export interface UseSectionScrollBottomOptions {
  /** CSS selector for the scrollable ancestor to listen on, found via `closest()` from `rootRef`
   *  (e.g. ".procedure-container", ".user-container"). `closest()` also matches `rootRef`'s own element,
   *  so a screen whose scroller IS its own root can pass the same class it renders. */
  scrollContainerSelector: string;
  /** Called when the container scrolls near its bottom, and once more whenever `itemsLength` changes if
   *  the container still isn't scrollable at all (no scroll event would otherwise ever fire). Always the
   *  LATEST value is used internally -- passing a new function identity every render never re-attaches
   *  the listener (the same "latest ref" idiom used elsewhere in this app for a handler whose identity
   *  would otherwise change on every content-neutral state update).
   *  Gated so it fires at most once per page: see `isPageLoading` and `itemsLength` below. */
  onNearBottom: () => void;
  /** false tears down any existing listener and skips every check -- e.g. semantic search mode (no
   *  paging) or a view with no pagination at all. */
  active: boolean;
  /** The number of items currently rendered in the section. Two jobs: (1) re-runs the "container isn't
   *  scrollable yet" check below after a page loads, and (2) clears the in-flight gate on a genuine list
   *  reset (e.g. a filter/mode change) even if that never passes through an `isPageLoading` true->false
   *  transition this hook itself observed. Changing it does not re-attach the scroll listener. */
  itemsLength: number;
  /** True while the page this hook last asked for is loading, false once it settles -- on EITHER success
   *  or failure (e.g. `isBottomLoading` from `useLoadingBottom`, not bare `isLoading` from `useLoader`,
   *  which also covers unrelated in-flight requests such as `getTeams` and would delay the transition).
   *  Its true->false transition is the PRIMARY way the in-flight gate reopens: `itemsLength` alone would
   *  leave it closed forever after a load that ends without changing the item count -- a failure (a
   *  reducer with no failure handler at all leaves the item count untouched) or a genuinely
   *  empty-but-not-last page. */
  isPageLoading: boolean;
}

export interface UseSectionScrollBottomResult {
  /** Attach to the feature's own root element -- the scrollable ancestor is found from here via
   *  `closest(scrollContainerSelector)`, so this element (or one of its descendants) must actually be
   *  inside that ancestor for the listener to attach. */
  rootRef: RefObject<HTMLDivElement>;
  /** Scrolls the resolved scroll container (the same `closest(scrollContainerSelector)` element the hook
   *  listens on) back to the top -- for a reload that replaces the whole list (a search or filter change)
   *  rather than appends to it (a next-page load), so the busy state/first rows aren't left off-screen
   *  below wherever the page had scrolled to. A no-op if the container hasn't resolved (e.g. `rootRef`
   *  hasn't mounted yet, or isn't inside `scrollContainerSelector` at all). */
  scrollToTop: () => void;
}

/**
 * Drives "load the next page" from a page/section's own scroll position instead of a table's inner
 * scroll, for views whose table no longer scrolls internally (only the section does).
 *
 * Pattern for the listener itself: `src/containers/Chat/hooks/useScrollToBottom.ts` (passive + rAF-
 * throttled scroll listener). Near-bottom check: `src/containers/Chat/components/ChatList/ChatList.tsx:56-61`.
 *
 * Gating: `requestAnimationFrame` throttles a single scroll *gesture*, but a burst of frames can still
 * land while the scroll position stays within the threshold (nothing moves until the next page renders),
 * calling `onNearBottom` many times for what should be one page load -- against a `takeLatest`-driven
 * saga with an appending reducer, the extra calls each bump `page` further and cancel the previous
 * request, so pages get skipped. `isFetchingRef` allows at most one call per page: set the instant a call
 * fires, and cleared on `isPageLoading`'s true->false transition (the load ending, whatever the outcome)
 * or on an `itemsLength` change (a list reset) -- see each option's own doc for why both are needed.
 */
export const useSectionScrollBottom = ({
  scrollContainerSelector,
  onNearBottom,
  active,
  itemsLength,
  isPageLoading,
}: UseSectionScrollBottomOptions): UseSectionScrollBottomResult => {
  const rootRef = useRef<HTMLDivElement>(null);
  const onNearBottomRef = useRef(onNearBottom);
  onNearBottomRef.current = onNearBottom;
  const isFetchingRef = useRef(false);
  const wasPageLoadingRef = useRef(isPageLoading);

  useEffect(() => {
    if (!active) return;
    const container = rootRef.current?.closest(scrollContainerSelector);
    if (!container) return;

    const checkNearBottom = () => {
      if (isFetchingRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < SECTION_SCROLL_BOTTOM_THRESHOLD_PX) {
        isFetchingRef.current = true;
        onNearBottomRef.current();
      }
    };
    const { schedule, cancel } = createRafThrottle();
    const throttledCheck = () => schedule(checkNearBottom);

    container.addEventListener("scroll", throttledCheck, { passive: true });
    return () => {
      container.removeEventListener("scroll", throttledCheck);
      cancel();
    };
  }, [active, scrollContainerSelector]);

  // PRIMARY gate reopen: `isPageLoading` going true -> false means the load this hook is waiting on has
  // ENDED, whatever the outcome. A ref (not just depending on `isPageLoading` directly) distinguishes a
  // real transition from every other render where it's simply `false` -- e.g. the initial mount, or the
  // one-tick delay before it first flips `true` -- neither of which should reopen a gate that was never
  // (yet) closed. Declared BEFORE the "short page" effect below so it runs first within the same commit
  // when both fire together (React runs a component's effects in the order they were registered).
  useEffect(() => {
    if (wasPageLoadingRef.current && !isPageLoading) {
      isFetchingRef.current = false;
    }
    wasPageLoadingRef.current = isPageLoading;
  }, [isPageLoading]);

  // SECONDARY reopen, for a genuine list reset: a change in `itemsLength` this hook didn't get to observe
  // an `isPageLoading` transition for (e.g. the request that populated it was already in flight before
  // this hook became active) still clears the gate. Declared before the "short page" effect for the same
  // ordering reason as above.
  useEffect(() => {
    isFetchingRef.current = false;
  }, [itemsLength]);

  // A short first (or only) page can leave the container not scrollable at all, so no scroll event would
  // ever fire to load the next one -- re-checked whenever the rendered item count changes (i.e. after a
  // page loads), not just once on mount.
  useEffect(() => {
    if (!active || isFetchingRef.current) return;
    const container = rootRef.current?.closest(scrollContainerSelector);
    if (!container) return;
    if (container.scrollHeight <= container.clientHeight) {
      isFetchingRef.current = true;
      onNearBottomRef.current();
    }
  }, [active, itemsLength, scrollContainerSelector]);

  const scrollToTop = useCallback(() => {
    const container = rootRef.current?.closest(scrollContainerSelector);
    if (container) container.scrollTop = 0;
  }, [scrollContainerSelector]);

  return { rootRef, scrollToTop };
};

export default useSectionScrollBottom;
